import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import ActionsTable from 'components/IAP/ActionsTable';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ActionViewModal from 'components/IAP/ActionViewModal';
import { INCIDENT, ORGANIZATION } from 'components/IAP/ICSConstants';
import {
  keys as incidentActionKeys,
  openInstantiatedIncidentTask,
  useInstantiateIncidentTask,
} from 'components/IAP/hooks/incidentActionHooks';
import { useHwcgFlag } from 'utils/featureFlags';
import { keys as orgActionKeys } from 'components/IAP/hooks/orgActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import { cn } from '@/lib/utils';

type SitrepTasksActionsPanelProps = {
  incidentId: string | null | undefined;
  aorId?: string | null;
  canEdit?: boolean;
  /**
   * Frozen tasks for the approved snapshot. When set, the table shows these
   * rows instead of the current actions.
   */
  snapshotActions?: ActionEntity[] | null;
  className?: string;
  /**
   * Bump when section text/version changes so the table refetches after AI
   * generate.
   */
  refreshKey?: string | number | null;
};

/**
 * Embeds the Actions table/drawer under an OPORD Tasks sitrep section.
 * Incident-scoped reports use incident actions; AOR-only reports use org
 * actions. Action count for AI generate is configured in section settings.
 */
const SitrepTasksActionsPanel: React.FC<SitrepTasksActionsPanelProps> = ({
  incidentId,
  aorId,
  canEdit = true,
  snapshotActions,
  className,
  refreshKey,
}) => {
  const queryClient = useQueryClient();
  const hwcg = useHwcgFlag();
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionEntity | null>(
    null
  );
  const [viewAction, setViewAction] = useState<ActionEntity | null>(null);
  const isOrgScope = !incidentId;
  const scope = isOrgScope ? ORGANIZATION : INCIDENT;
  const { instantiate, isPending: creatingTask } = useInstantiateIncidentTask(
    isOrgScope ? undefined : incidentId
  );

  const addAction = () => {
    if (isOrgScope || !hwcg) {
      setShowAddDrawer(true);
      return;
    }
    if (creatingTask) return;
    void openInstantiatedIncidentTask({
      instantiate,
      status: 'Planned',
      aorId,
      onOpen: (row) => {
        setSelectedAction(row);
        setShowEditDrawer(true);
      },
    });
  };

  useEffect(() => {
    if (isOrgScope) {
      void queryClient.invalidateQueries({
        queryKey: orgActionKeys.base,
      });
      void queryClient.refetchQueries({
        queryKey: orgActionKeys.base,
      });
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: incidentActionKeys.base,
    });
    void queryClient.refetchQueries({
      queryKey: incidentActionKeys.base,
    });
  }, [incidentId, isOrgScope, refreshKey, queryClient]);

  const handleEditAction = useCallback((action: ActionEntity) => {
    setSelectedAction(action);
    setShowEditDrawer(true);
  }, []);

  const handleViewAction = useCallback((action: ActionEntity) => {
    setViewAction(action);
  }, []);

  const closeViewModal = useCallback(() => {
    setViewAction(null);
  }, []);

  return (
    <div className={cn('tw:mt-3 tw:space-y-3', className)}>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <h6 className="tw:m-0 tw:text-sm tw:font-semibold">
          {isOrgScope ? 'Org Actions' : 'Incident Actions'}
        </h6>
        {canEdit ? (
          <Button
            type="button"
            size="sm"
            disabled={creatingTask}
            onClick={addAction}
          >
            Add Action
          </Button>
        ) : null}
      </div>

      <ActionsTable
        scope={scope}
        incidentIdOverride={isOrgScope ? undefined : incidentId}
        canEdit={canEdit}
        filteredActions={snapshotActions ?? undefined}
        onEditAction={handleEditAction}
        onViewAction={handleViewAction}
      />

      {showAddDrawer && canEdit && (isOrgScope || !hwcg) ? (
        <ActionDrawer
          scope={scope}
          incidentIdOverride={isOrgScope ? undefined : incidentId}
          defaultAorId={aorId}
          toggle={() => setShowAddDrawer(false)}
        />
      ) : null}

      {showEditDrawer && selectedAction && canEdit ? (
        <ActionDrawer
          scope={scope}
          incidentIdOverride={isOrgScope ? undefined : incidentId}
          selectedEntity={selectedAction}
          toggle={() => {
            setShowEditDrawer(false);
            setSelectedAction(null);
          }}
        />
      ) : null}

      <ActionViewModal
        show={!!viewAction}
        action={viewAction}
        onClose={closeViewModal}
        canEdit={canEdit}
        onEdit={handleEditAction}
      />
    </div>
  );
};

export default SitrepTasksActionsPanel;
